import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import { DOWNLOAD_ROUTE, MYPAGE_ROUTE } from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { CheckmarkCircleIcon, CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import { Helmet } from "react-helmet";
import { PaymentHistoryWithLicenses } from "../types/PaymentHistoryWithLicenses";
import { formatUtcToLocalDateTime } from "../utils/DateUtils";

interface LocationState {
    issuedResult: PaymentHistoryWithLicenses;
}

/**
 * ライセンス注文完了画面（無料体験版用）
 * @returns JSX.Element
 */
const ProductCompleteTrial = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user } = useUserContext();
    const location = useLocation();
    const { issuedResult } = (location.state as LocationState) || {};
    const [errorMessage] = useState<string>("");

    // ページ表示時にuseEffectが2回実行されるのを防ぐために使用。
    // ※React.StrictModeの仕様
    const effectRun = useRef(false);

    // ページ初期化処理
    useEffect(() => {
        if (!user) {
            // ページリロード時はコンテキストも初期化されるため、空の場合は一度リターンする
            // コンテキストが生成されユーザー情報が生成された時点で再実行する
            return;
        }
        if (!effectRun.current) {
            return () => {
                effectRun.current = true;
            };
        }
    }, [user, navigate]);

    /**
     * ダウンロード画面へ押下時のイベント処理
     */
    const handleGoToDouwnload = () => {
        navigate(DOWNLOAD_ROUTE);
    };

    /**
     * マイページへ戻る押下時のイベント処理
     */
    const handleBackToMyPage = () => {
        navigate(MYPAGE_ROUTE);
    };

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title={t("ProductCompleteForTrial.meta.title")} />
            <ScrollToTop />
            <DFNavbar bottomPadding={true} />
            <div className="mx-auto max-w-3xl flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">{t("ProductCompleteForTrial.productCompleteTitle")}</h1>

                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center p-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {/* 注文完了メッセージ */}
                <div className="flex items-center justify-center pb-4 pt-4">
                    <CheckmarkCircleIcon />
                    <span className="pl-2 text-center text-xl font-bold text-green-500">
                        {t("ProductCompleteForTrial.productCompleteMessage")}
                    </span>
                </div>
                <ul className="ml-10 list-disc pb-4 font-bold text-gray-600">
                    <li>
                        <Trans i18nKey={"ProductCompleteForTrial.productCompleteText1"} />
                    </li>
                    <li>
                        <Trans i18nKey={"ProductCompleteForTrial.productCompleteText2"} />
                    </li>
                </ul>

                {/* 発行結果表示パネル */}
                {issuedResult && (
                    <div className="mx-auto flex max-w-2xl items-center justify-center font-bold">
                        <div className="min-w-full rounded-lg border border-gray-200 bg-green-50 p-2 shadow">
                            <div className="grid grid-cols-4 items-center justify-center py-2">
                                {/* DFアカウント */}
                                <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                    {t("CommonLabel.email")}
                                </div>
                                <div className="col-span-3 py-1">{issuedResult?.p_email}</div>
                                {/* 商品名 */}
                                <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                    {t("CommonLabel.packageName")}
                                </div>
                                <div className="col-span-3 py-1">{issuedResult.licenses[0].package_name}</div>
                                {/* ライセンスキー */}
                                <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                    {t("CommonLabel.licenseKey")}
                                </div>
                                <div className="col-span-3 py-1">{issuedResult.licenses[0].license_key}</div>
                                {/* 利用開始日時 */}
                                <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                    {t("CommonLabel.activationDatetime")}
                                </div>
                                <div className="col-span-3 py-1">
                                    {formatUtcToLocalDateTime(issuedResult.licenses[0].activation_date)}
                                </div>
                                {/* 有効期限日時 */}
                                <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                    {t("CommonLabel.expirationDatetime")}
                                </div>
                                <div className="col-span-3 py-1">
                                    {formatUtcToLocalDateTime(issuedResult.licenses[0].expire_date)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ご利用の流れ */}
                <div className="mx-auto flex max-w-2xl items-center justify-center">
                    <div className="my-4 rounded-lg border border-gray-300 p-4 shadow">
                        <div className="mb-2 text-center text-lg font-bold">
                            {t("ProductCompleteForTrial.flowOfUsageTitle")}
                        </div>
                        <ul className="ml-5 list-decimal text-sm text-gray-800">
                            <li>{t("ProductCompleteForTrial.flowOfUsageText1")}</li>
                            <li className="pt-1">{t("ProductCompleteForTrial.flowOfUsageText2")}</li>
                            <li className="pt-1">{t("ProductCompleteForTrial.flowOfUsageText3")}</li>
                            <li className="pt-1">{t("ProductCompleteForTrial.flowOfUsageText4")}</li>
                        </ul>
                    </div>
                </div>

                {/* ダウンロードページへ移動 */}
                <div className="flex justify-evenly pb-2 pt-5 text-sm font-medium">
                    <button onClick={handleGoToDouwnload} className="btn-primary w-1/3">
                        {t("ProductCompleteForTrial.goToUserLicense")}
                    </button>
                </div>

                {/* マイページへ戻る */}
                <div className="flex justify-evenly pb-5 pt-2 text-sm font-medium">
                    <button onClick={handleBackToMyPage} className="btn-secondary w-1/3">
                        {t("ProductCompleteForTrial.backToMyPage")}
                    </button>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ProductCompleteTrial;
